import { ChangeDetectionStrategy, Component, computed, contentChild, input, output, type Signal, type TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';

export interface StepperDialogStep {
  id: string;
  labelKey: string;
  labelParams?: Record<string, unknown>;
  valid: Signal<boolean>;
}

@Component({
  selector: 'zag-stepper-dialog',
  imports: [NgTemplateOutlet, TranslatePipe, ZagButton, ZagIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './stepper-dialog.component.html',
  styleUrl: './stepper-dialog.component.scss',
  host: {
    class: 'zag-stepper-dialog-host',
    role: 'dialog',
    'aria-modal': 'true',
    '[attr.aria-labelledby]': 'titleId',
  },
})
export class StepperDialogComponent {
  readonly steps = input.required<StepperDialogStep[]>();
  readonly activeStepId = input.required<string>();
  readonly titleKey = input.required<string>();
  readonly titleParams = input<Record<string, unknown>>();
  readonly backLabelKey = input('common.back');
  readonly nextLabelKey = input('common.next');
  readonly submitLabelKey = input('common.save');
  readonly submitLabelParams = input<Record<string, unknown>>({});
  readonly cancelLabelKey = input('common.cancel');
  readonly stepPositionKey = input('stepperDialog.stepIndicator');
  readonly isSubmitting = input(false);

  readonly stepChange = output<string>();
  readonly submitted = output<void>();
  readonly cancelled = output<void>();

  readonly stepContent = contentChild<TemplateRef<{ $implicit: string }>>('stepContent');

  protected readonly titleId = `zag-stepper-dialog-title-${nextStepperDialogId()}`;
  protected readonly stepCount = computed(() => this.steps().length);
  protected readonly activeIndex = computed(() =>
    Math.max(
      0,
      this.steps().findIndex(s => s.id === this.activeStepId())
    )
  );
  protected readonly activeStep = computed(() => this.steps()[this.activeIndex()]);
  protected readonly isFirst = computed(() => this.activeIndex() === 0);
  protected readonly isLast = computed(() => this.activeIndex() === this.stepCount() - 1);
  protected readonly canGoNext = computed(() => this.activeStep()?.valid() ?? false);

  protected back(): void {
    if (this.isFirst() || this.isSubmitting()) return;
    this.stepChange.emit(this.steps()[this.activeIndex() - 1].id);
  }

  protected next(): void {
    if (this.isLast() || !this.canGoNext() || this.isSubmitting()) return;
    this.stepChange.emit(this.steps()[this.activeIndex() + 1].id);
  }

  protected goToStep(id: string): void {
    if (this.isSubmitting() || id === this.activeStepId()) return;
    const target = this.steps().findIndex(s => s.id === id);
    if (target < 0) return;
    if (
      target > this.activeIndex() &&
      !this.steps()
        .slice(0, target)
        .every(s => s.valid())
    )
      return;
    this.stepChange.emit(id);
  }

  protected onSubmit(): void {
    if (!this.isLast() || !this.canGoNext() || this.isSubmitting()) return;
    this.submitted.emit();
  }

  protected onCancel(): void {
    if (this.isSubmitting()) return;
    this.cancelled.emit();
  }
}

let stepperDialogIdCounter = 0;

function nextStepperDialogId(): number {
  stepperDialogIdCounter += 1;
  return stepperDialogIdCounter;
}
